import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViewOrderContext } from "../../../contexts/ViewOrderContext";
import { loadReferenceLetters } from "../../../services/referenceLetterService";
import { ReferenceLetter } from "../../../interfaces/referenceLetter";
import ToolTip from "../../../components/component-tools/ToolTip";
import NotifyClient from "../../../components/order-components/NotifyClient";
import { formatDate } from "../../../services/helpers";
import UploadSendFinalReport from "../../../components/order-components/UploadSendFinalReport";
import SendFinalReport from "../../../components/order-components/SendFinalReport";
import { NotificationType } from "../../../interfaces/clientNotification";
import { deleteReferenceOrder } from "../../../services/referenceOrderService";
import ConfirmDelete from "../../../components/component-tools/ConfirmDelete";

interface ReferenceOrderSectionProps {
  handleInputChange: (e: any, field: string, type?: string) => void;
  getServiceNames: (type: number) => string[];
}

const ReferenceOrderSection: React.FC<ReferenceOrderSectionProps> = ({
  handleInputChange,
  getServiceNames,
}) => {
  const navigate = useNavigate();
  const [showNotifyCandidatePopup, setShowNotifyCandidatePopup] =
    useState(false);
  const [showReferenceFinalReportPopup, setShowReferenceFinalReportPopup] =
    useState(false);
  const [
    showReferenceUploadFinalReportPopup,
    setShowReferenceUploadFinalReportPopup,
  ] = useState(false);
  const [allLetters, setAllLetters] = useState<
    Record<number, ReferenceLetter[]>
  >({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  const {
    order,
    referenceOrder,
    setReferenceOrder,
    referenceDetails,
    setReferenceDetails,
    client,
    referenceFinalUrl,
    candidateNotifiedDates,
  } = useViewOrderContext();

  useEffect(() => {
    const fetchReferenceLetters = async () => {
      try {
        if (referenceDetails && referenceDetails.length > 0) {
          const lettersMap = await loadReferenceLetters(referenceDetails);
          setAllLetters(lettersMap);
        }
      } catch (error) {
        console.error("Error fetching reference letters:", error);
      }
    };
    fetchReferenceLetters();
  }, [referenceDetails]);

  if (!referenceOrder || referenceOrder.referenceOrderID === 0) return null;

  const handleDelete = async () => {
    setConfirmDeleteOpen(false);
    setLoadingSpinner(true);

    try {
      await deleteReferenceOrder(referenceOrder.referenceOrderID);
      setReferenceOrder(undefined);
      setReferenceDetails([]);
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="data-container">
      <div className="text-container-header" style={{ flex: "0 0 30%" }}>
        Reference Service Requested
      </div>
      <div
        style={{
          flex: "0 0 65%",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: "20px" }}>
          <div className="field-long" style={{ flex: "0 0 20%" }}>
            <button
              className={`oxbtn btn-new ${order.orderStatusID === 5 ? "disabled" : ""}`}
              disabled={order.orderStatusID === 5}
              onClick={() => {
                localStorage.setItem("scrollY", window.scrollY.toString());
                navigate("/admin/add-reference", {
                  state: {
                    referenceOrderID: referenceOrder.referenceOrderID,
                  },
                });
              }}
            >
              <div> New Reference </div>
              <i className="bi bi-plus-circle-fill plus-icon"></i>
            </button>
          </div>
          <div className="field-long" style={{ flex: "0 0 15%" }}>
            <label className="field-label">Years:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={referenceOrder.years}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="checkbox-long">
            <div className="field-input" style={{ flex: "0 0 3%" }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  referenceOrder.adminReviewed
                    ? referenceOrder.adminReviewed
                    : false
                }
                onChange={(e) =>
                  handleInputChange(e, "adminReviewed", "reference")
                }
                disabled={order.orderStatusID === 5}
              />
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <label className="field-label">Admin Reviewed </label>
              <ToolTip
                tooltipText="Please check this box if admin has completed all action items on this order. i.e. sent all reference questionnaires out."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
          </div>

          <div className="checkbox-long">
            <div className="field-input" style={{ flex: "0 0 3%" }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  referenceOrder.inAdminReview
                    ? referenceOrder.inAdminReview
                    : false
                }
                onChange={(e) =>
                  handleInputChange(e, "inAdminReview", "reference")
                }
                disabled={order.orderStatusID === 5}
              />
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <label className="field-label">In Review</label>
              <ToolTip
                tooltipText="Please check this box if an admin is currently working on this piece of the order."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
            <div></div>
          </div>
        </div>

        {confirmDeleteOpen && (
          <ConfirmDelete
            onConfirm={() => handleDelete()}
            onCancel={() => {
              setConfirmDeleteOpen(false);
            }}
            message="Delete Reference Order? All details will be deleted."
          />
        )}
        {Object.keys(allLetters).length === 0 && (
          <div className="close-button-container">
            <button
              className="delete-button"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
      </div>
      <div style={{ flex: "0 0 100%" }}>
        <ul>
          {getServiceNames(2).map((serviceName, index) => (
            <li key={index}>{serviceName}</li>
          ))}
        </ul>
      </div>
      <div style={{ display: "flex", gap: "10px", flex: "0 0 100%" }}>
        <div>
          <button
            className={`smbtn btn-grey ${!referenceOrder ? "disabled" : ""}`}
            onClick={() =>
              setShowNotifyCandidatePopup(!showNotifyCandidatePopup)
            }
            disabled={!referenceOrder}
          >
            Notify Candidate <i className="btn-icon bi bi-envelope"></i>
          </button>
        </div>
        <ToolTip
          tooltipText="Notifies the candidate of unresponsive references."
          placement="right"
          className="info-icon-spec"
        ></ToolTip>

        <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
          {candidateNotifiedDates?.map((date) => (
            <div key={date.notifiedDateID}>
              {formatDate(date.notifiedDate, "us")}
            </div>
          ))}
        </div>
      </div>
      {showNotifyCandidatePopup && referenceOrder && (
        <NotifyClient
          orderId={order.orderID}
          clientId={order.clientID}
          candidateEmail={order.candidateEmail ?? ""}
          type={NotificationType.NotifyCandidate}
          onClose={() => {
            setShowNotifyCandidatePopup(false);
            window.location.reload();
          }}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        <div className="field-long">
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Invoice Date:</label>
            <input
              className="form-control"
              type="date"
              value={formatDate(referenceOrder.invoiceDate)}
              disabled
            />
          </div>
          <div className="field-stacked" style={{ flex: "0 0 10%" }}>
            <label className="field-label">Invoice Amt:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={
                  referenceOrder.invoiceAmount
                    ? referenceOrder.invoiceAmount
                    : ""
                }
                onChange={(e) => handleInputChange(e, "invoiceAmount")}
                disabled={referenceOrder.billedDate != null}
              />
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Billed Date:</label>
            <input
              className="form-control"
              type="date"
              value={formatDate(referenceOrder.billedDate)}
              disabled
            />
          </div>
        </div>
        <div className="field-long" style={{ alignItems: "end" }}>
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Final Report Date:</label>
            <input
              className="form-control"
              type="date"
              value={formatDate(referenceOrder.finalReportDate)}
              disabled
            />
          </div>
          {referenceFinalUrl && (
            <div>
              <button
                style={{ textWrap: "nowrap" }}
                className="oxbtn btn-purple"
                onClick={() => {
                  if (referenceFinalUrl) {
                    window.open(referenceFinalUrl, "_blank");
                  }
                }}
              >
                View Final Report
                <i className="btn-icon bi bi-box-arrow-up-right"></i>
              </button>
            </div>
          )}

          <div>
            {referenceOrder.finalReportNote?.trim() && (
              <div
                style={{
                  color: "red",
                  fontSize: "14px",
                  width: "200px",
                  textAlign: "center",
                }}
              >
                <b>{referenceOrder.finalReportNote}</b>
              </div>
            )}

            {referenceOrder.referenceTypeID === 1 ? (
              <div>
                <button
                  className={`oxbtn btn-grey ${order.orderStatusID === 5 ? "disabled" : ""}`}
                  onClick={() => setShowReferenceUploadFinalReportPopup(true)}
                  disabled={order.orderStatusID === 5}
                >
                  Upload & Send Final Report
                  <i className="btn-icon bi bi-send"></i>
                </button>

                {showReferenceUploadFinalReportPopup && client && (
                  <UploadSendFinalReport
                    client={client}
                    order={order}
                    referenceOrder={referenceOrder}
                    onClose={() => {
                      setShowReferenceUploadFinalReportPopup(false);
                      window.location.reload();
                    }}
                  />
                )}
              </div>
            ) : (
              <div>
                <button
                  className={`oxbtn btn-grey ${order.orderStatusID === 5 ? "disabled" : ""}`}
                  onClick={() => setShowReferenceFinalReportPopup(true)}
                  disabled={order.orderStatusID === 5}
                >
                  Send Final Report
                  <i className="btn-icon bi bi-send"></i>
                </button>

                {showReferenceFinalReportPopup && client && (
                  <SendFinalReport
                    client={client}
                    order={order}
                    referenceOrder={referenceOrder}
                    referenceDetails={referenceDetails}
                    onClose={() => {
                      setShowReferenceFinalReportPopup(false);
                      window.location.reload();
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <div className="table-container">
          <table className="table" style={{ cursor: "pointer" }}>
            <thead>
              <tr>
                <th style={{ width: "250px" }}>Entity Name</th>
                <th style={{ width: "200px" }}>Contact Name</th>
                <th style={{ width: "150px" }}>Last Letter</th>
                <th style={{ width: "100px" }}>Certified</th>
                <th style={{ width: "150px" }}>Received</th>
                <th style={{ width: "100px" }}>Red Flag</th>
                <th style={{ width: "150px" }}>Client Notified</th>
                <th style={{ width: "150px" }}>Admin Reviewed</th>
                <th style={{ width: "50px" }}></th>
              </tr>
            </thead>
            <tbody>
              {referenceDetails?.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center" }}>
                    No reference details found.
                  </td>
                </tr>
              ) : (
                referenceDetails?.map((detail) => {
                  const letters = allLetters[detail.referenceDetailID] || [];
                  const lastLetter =
                    letters.length > 0 ? letters[letters.length - 1] : null;
                  return (
                    <tr
                      key={detail.referenceDetailID}
                      onClick={() => {
                        localStorage.setItem(
                          "scrollY",
                          window.scrollY.toString(),
                        );
                        navigate(
                          `/admin/add-reference/${detail.referenceDetailID}`,
                          {
                            state: {
                              referenceOrderID: referenceOrder.referenceOrderID,
                              referenceDetailID: detail.referenceDetailID,
                              closed:
                                referenceOrder.finalReportUrl !== null &&
                                referenceOrder.finalReportUrl !== ""
                                  ? true
                                  : false,
                            },
                          },
                        );
                      }}
                    >
                      <td>{detail.placeName}</td>
                      <td>{detail.referenceName}</td>
                      <td>
                        {lastLetter?.letterDate
                          ? formatDate(lastLetter?.letterDate, "us")
                          : "-"}
                      </td>
                      <td className="blue-text">
                        <b>
                          {" "}
                          {letters.some((letter) => letter.certified)
                            ? "Cert"
                            : ""}
                        </b>
                      </td>
                      <td>
                        {detail.letterReturned
                          ? formatDate(detail.letterReturned, "us")
                          : "-"}
                      </td>
                      <td
                        style={{
                          color: detail.redFlag ? "red" : "inherit",
                          fontWeight: detail.redFlag ? "bold" : "normal",
                        }}
                      >
                        {detail.redFlag ? "Red Flag" : ""}
                      </td>
                      <td>
                        {detail.clientNotifiedDate
                          ? formatDate(detail.clientNotifiedDate, "us")
                          : "-"}
                      </td>
                      <td>{detail.adminReviewed ? "Yes" : ""}</td>
                      <td>
                        {detail?.alert?.trim() && (
                          <ToolTip
                            tooltipText={`${detail.alert}\n${formatDate(detail?.alertLastUpdatedDate, "datetime")}`}
                            placement="top"
                            className="alert-sticky"
                          >
                            <i className="bi bi-stickies-fill"></i>
                          </ToolTip>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default ReferenceOrderSection;
